/**
 * Diverse model panel selection — the core of cross-vendor "second opinion"
 * and consensus (#1).
 *
 * The value proposition only exists because Elyra is provider-agnostic: it can
 * ask models from *different vendors* to review or corroborate each other. A
 * single-vendor agent can only ever ask the same family to check its own work,
 * which shares the same blind spots. Diversity of vendors is the point.
 *
 * Pure and unit-tested, like the failover core it builds on.
 */

import { costBand, type FallbackModelInfo, modelKey } from "./model-fallback.js";

/**
 * Pick up to `count` models to form a review/consensus panel for the primary
 * model, each from a distinct provider.
 *
 * Diversity first: providers other than the primary's are preferred, so the
 * panel genuinely spans vendors rather than echoing the primary. Within a
 * provider only the single best-matched model is considered (closest
 * capability band, reasoning match, then cost), so the panel is broad rather
 * than several near-duplicates from one vendor.
 *
 * Image conversations only ever include vision-capable reviewers, so a
 * multimodal task can't put a text-only model on the panel.
 */
export function selectDiverseModels<T extends FallbackModelInfo>(
	primary: FallbackModelInfo,
	available: readonly T[],
	count: number,
): T[] {
	if (count <= 0) return [];

	const needsImage = primary.input.includes("image");
	const primaryBand = costBand(primary.cost.output);

	const candidates = available.filter(
		(m) => modelKey(m) !== modelKey(primary) && (!needsImage || m.input.includes("image")),
	);

	// Keep only the best-matched model per provider.
	const bestByProvider = new Map<string, T>();
	for (const m of candidates) {
		const current = bestByProvider.get(m.provider);
		if (
			!current ||
			capabilityCloseness(m, primary, primaryBand) < capabilityCloseness(current, primary, primaryBand)
		) {
			bestByProvider.set(m.provider, m);
		}
	}

	// Order providers: different vendor than the primary first, then by how
	// closely their representative matches the primary's capability.
	const reps = [...bestByProvider.values()].sort((a, b) => {
		const aOther = a.provider === primary.provider ? 1 : 0;
		const bOther = b.provider === primary.provider ? 1 : 0;
		if (aOther !== bOther) return aOther - bOther;
		return capabilityCloseness(a, primary, primaryBand) - capabilityCloseness(b, primary, primaryBand);
	});

	return reps.slice(0, count);
}

/** Lower is closer. Prefers same band, reasoning match, then nearest cost. */
function capabilityCloseness(m: FallbackModelInfo, primary: FallbackModelInfo, primaryBand: number): number {
	const band = Math.abs(costBand(m.cost.output) - primaryBand);
	const reasoning = m.reasoning === primary.reasoning ? 0 : 1;
	const cost = Math.abs(m.cost.output - primary.cost.output);
	// Weight so band dominates, then reasoning, then cost distance.
	return band * 1000 + reasoning * 100 + Math.min(cost, 99);
}
